import debug from "debug";
import ButtonComponent from "../ui/ButtonComponent";
import InputComponent from "../ui/InputComponent";
import ModalComponent from "../ui/ModalComponent";
import { getConfigForMode } from "./config";
import { ContextMenuModalData } from "./ContextMenuModalData";

const log = debug("app:cm");

/**
 * Manages the user interface for the context menu modal.
 * Handles initialization, data binding, and event-driven interactions.
 * @category Components
 * @subcategory ContextMenu
 */
class ContextMenuModalUI {
  /**
   * Creates an instance of ContextMenuModalUI.
   * @param {Object} eventHub - The event hub for global event handling.
   */
  constructor(eventHub) {
    /**
     * Event hub for emitting and listening to events.
     * @type {Object}
     */
    this.eventHub = eventHub;

    /**
     * Stores the original data for comparison with edited data.
     * @type {Object|null}
     */
    this.originalData = null;

    /**
     * Stores the current data being edited in the modal.
     * @type {Object|null}
     */
    this.data = null;

    /**
     * Stores the input components used in the modal.
     * @type {Array<InputComponent>}
     */
    this.inputs = [];

    this.setupEventListener();
  }

  /**
   * Sets up event listeners for initializing and resetting the modal.
   */
  setupEventListener() {
    this.eventHub.on("openContextMenuModal", this.initialize.bind(this));
    this.eventHub.on("resetToDefault", this.resetData.bind(this));
  }

  cleanup() {
    console.log("ContextMenuModalUI cleanup started...");
  
    // 1. Remove event listeners from eventHub
    if (this.handleOpenContextMenuModal) {
      this.eventHub.off("openContextMenuModal", this.handleOpenContextMenuModal);
    }
    if (this.handleResetToDefault) {
      this.eventHub.off("resetToDefault", this.handleResetToDefault);
    }
  
    // 2. Dispose UI components
    if (this.modal) {
      this.modal.dispose();
      this.modal = null;
    }
  
    this.inputs.forEach((input) => {
      if (input.dispose) input.dispose();
    });
    this.inputs = [];
  
    if (this.resetButton && this.resetButton.dispose) {
      this.resetButton.dispose();
      this.resetButton = null;
    }
  
    if (this.saveButton && this.saveButton.dispose) {
      this.saveButton.dispose();
      this.saveButton = null;
    }
  
    // 3. Clear object references
    this.originalData = null;
    this.data = null;
    this.config = null;
    this.mode = null;
  
    console.log("ContextMenuModalUI cleanup completed.");
  }
  /**
   * Initializes the modal with the given configuration and properties.
   * @param {Object} params - Configuration parameters.
   * @param {string} [params.mode="default"] - The mode of the modal.
   * @param {Object} params.properties - The properties to display and edit.
   */
  initialize({ mode = "default", properties }) {
    if (this.modal) this.dispose();

    log("Initializing modal with data", mode, properties);

    /**
     * The mode of the modal (e.g., "default", "cfg-part").
     * @type {string}
     */
    this.mode = mode;

    /**
     * Configuration for the current mode.
     * @type {Object}
     */
    this.config = getConfigForMode(this.mode);

    this.originalData = JSON.parse(JSON.stringify(properties));
    this.data = { ...properties };

    log(
      "initilaize this.data, this.originaldata",
      this.data,
      this.originalData
    );

    const modalContent = this.createModalContent();
    this.modal = new ModalComponent({
      content: modalContent,
      onClose: () => this.dispose(),
      draggable: true,
    });

    this.updateButtonStates();
  }

  /**
   * Creates the content for the modal.
   * @returns {HTMLElement} The modal content element.
   */
  createModalContent() {
    const modalContent = document.createElement("div");
    this.eventHub.emit("lockEvents", true);
    modalContent.appendChild(this.setupHeader());
    modalContent.appendChild(this.setupForm());
    modalContent.appendChild(this.setupFooter());
    return modalContent;
  }

  /**
   * Creates the header for the modal.
   * @returns {HTMLElement} The modal header element.
   */
  setupHeader() {
    const header = document.createElement("div");
    header.classList.add("modal-header");

    const headerTitle = document.createElement("div");
    headerTitle.classList.add("modal-header-title");
    headerTitle.textContent = "Properties";

    const closeButton = new ButtonComponent({
      imgSrc: "./imgs/Close.png",
      tooltip: "Close",
      onClick: () => this.dispose(),
      className: "close-button-modal",
    });

    header.appendChild(headerTitle);
    header.appendChild(closeButton.button);
    return header;
  }

  /**
   * Creates the form containing input fields.
   * @returns {HTMLElement} The form element.
   */
  setupForm() {
    const form = document.createElement("div");
    form.classList.add("form-container");

    /**
     * Array of input components.
     * @type {Array<InputComponent>}
     */
    this.inputs = this.createInputs();
    this.inputs.forEach((input) => form.appendChild(input.inputContainer));

    return form;
  }

  /**
   * Creates the footer for the modal with reset and save buttons.
   * @returns {HTMLElement} The modal footer element.
   */
  setupFooter() {
    const footer = document.createElement("div");
    footer.classList.add("footer-btn-container");

    this.resetButton = new ButtonComponent({
      label: "RESET",
      onClick: () => this.resetDataEmitEvent(),
      className: ["cm-reset-btn-inactive"],
      disabled: true,
    });

    this.saveButton = new ButtonComponent({
      label: "SAVE",
      onClick: () => this.handleSave(),
      className: ["cm-save-btn-inactive"],
      disabled: true,
    });

    footer.appendChild(this.resetButton.button);
    footer.appendChild(this.saveButton.button);
    return footer;
  }

  /**
   * Creates input fields for the modal based on data properties.
   * @returns {Array<InputComponent>} Array of input components.
   */
  createInputs() {
    return Object.entries(this.data).map(([label, value]) => {
      const staticField = this.config.staticFields[label];
      const isEditable = staticField
        ? staticField.editable
        : this.config.defaultEditable;
      const type = staticField ? staticField.type : this.config.defaultType;

      return new InputComponent({
        label,
        value,
        readOnly: !isEditable,
        type,
        className: !isEditable
          ? "cm-modal-input-non-editable"
          : "cm-modal-input-editable",
        onChange: (e) => {
          this.data[label] = e.target.value;
          this.updateButtonStates();
        },
      });
    });
  }

  /**
   * Updates the states of the reset and save buttons.
   */
  updateButtonStates() {
    const hasChanges =
      JSON.stringify(this.data) !== JSON.stringify(this.originalData);
    const isCfgPartMode = this.mode === "cfg-part" || this.mode === "user-cfg";

    this.toggleButtonState(
      this.saveButton,
      hasChanges,
      "cm-save-btn-active",
      "cm-save-btn-inactive"
    );

    this.toggleButtonState(
      this.resetButton,
      hasChanges || isCfgPartMode, // Active if data is changed or mode is "cfg-part"
      "cm-reset-btn-active",
      "cm-reset-btn-inactive"
    );
  }

  /**
   * Toggles the state of a button based on its active state.
   * @param {ButtonComponent} button - The button to update.
   * @param {boolean} isActive - Whether the button should be active.
   * @param {string} activeClass - CSS class for the active state.
   * @param {string} inactiveClass - CSS class for the inactive state.
   */
  toggleButtonState(button, isActive, activeClass, inactiveClass) {
    button.button.disabled = !isActive;
    button.button.classList.toggle(activeClass, isActive);
    button.button.classList.toggle(inactiveClass, !isActive);
  }

  /**
   * Emits an event to reset data to default values.
   */
  resetDataEmitEvent() {
    this.eventHub.emit("contextMenuReset", {
      data: this.originalData,
    });
  }

  /**
   * Resets the modal data to the given properties.
   * @param {Object} props - The properties to reset to.
   */
  resetData(props) {
    this.data = props;
    // Update originalData to match reset values so save button is disabled
    this.originalData = JSON.parse(JSON.stringify(props));
    log("reset data event", this.data);
    this.updateInputValues();
    this.updateButtonStates();
  }

  /**
   * Updates input field values based on current data.
   */
  updateInputValues() {
    log("this.inputs", this.inputs);
    this.inputs.forEach((input) => {
      const label = input.label;
      log("label", label);
      input.inputField.value = this.data[label];
    });
  }

  /**
   * Handles the save action and emits an event with updated data.
   */
  handleSave() {
    if (!this.saveButton.button.disabled) {
      this.eventHub.emit("contextMenuSave", {
        updatedData: this.data,
      });
      this.originalData = JSON.parse(JSON.stringify(this.data));
      this.updateButtonStates();
      this.dispose();
    }
  }

  /**
   * Disposes the modal and cleans up related resources.
   */
  dispose() {
    if (this.modal) {
      this.modal.dispose();
      this.modal = null;
      this.eventHub.emit("lockEvents", false);
    }
  }
}

export default ContextMenuModalUI;
